import { PROJECT_STATUSES } from '@/shared/constants/statuses';
import type { CalendarFilters, CalendarItem, ProjectStatus } from '@/shared/types/domain';

/**
 * Os filtros da US-13, aplicados **no cliente** (spec-calendar.md §9, decisão 54).
 *
 * Eles são conveniência de leitura, nunca de visibilidade: o que a pessoa
 * enxerga já foi decidido pela RLS e pela RPC, e remover um filtro nunca revela
 * uma linha nova. Filtrar aqui responde em zero milissegundos (RNF-P05, folgado
 * — não há round trip) e mantém UMA entrada de cache por mês.
 *
 * **Ocorrência de agenda fixa é atravessada por ministério, coordenação e
 * situação, e recortada por espaço.** Culto não tem ministério dono nem situação
 * de esteira; sumi-lo ao filtrar por "Ministério de Jovens" seria correto, mas
 * filtrar por coordenação e ver o Templo esvaziar faria a grade mentir sobre a
 * ocupação do prédio — que é o que o alerta de conflito existe para enxergar.
 * Por espaço ele responde, porque ali a pergunta é justamente sobre o prédio.
 */
export function applyFilters(
  items: readonly CalendarItem[],
  filters: CalendarFilters,
): readonly CalendarItem[] {
  const coordinations = setOf(filters.coordinationIds);
  const ministries = setOf(filters.ministryIds);
  const spaces = setOf(filters.spaceNames);
  const statuses = setOf(filters.statuses);

  return items.filter((item) => {
    if (item.kind === 'occurrence') {
      return !spaces || item.occurrence.space_names.some((name) => spaces.has(name));
    }

    const { project } = item;
    if (coordinations && !coordinations.has(project.coordination_id)) return false;
    if (ministries && !ministries.has(project.ministry_id)) return false;
    if (spaces && !project.space_names.some((name) => spaces.has(name))) return false;
    if (statuses && !statuses.has(project.status)) return false;
    return true;
  });
}

/**
 * A lista de escolhas como conjunto — e `undefined` quando não há escolha
 * nenhuma. É o que faz **lista vazia e campo ausente dizerem a mesma coisa**:
 * "não recortei esta dimensão", nunca "não quero ver nada".
 */
function setOf<T>(values: readonly T[] | undefined): Set<T> | null {
  return values && values.length > 0 ? new Set(values) : null;
}

export function hasAnyFilter(filters: CalendarFilters): boolean {
  return countActiveFilters(filters) > 0;
}

/**
 * Quantas CAIXAS estão marcadas — o número no botão `Filtrar`, ao lado do mês
 * (§18, decisão 106).
 *
 * Ele existe porque as escolhas vivem dentro de uma folha, e portanto ficam
 * ESCONDIDAS: sem o contador, uma grade filtrada é indistinguível de uma grade
 * vazia, e a pessoa conclui que o mês não tem nada marcado.
 *
 * Conta opções marcadas, não dimensões usadas: "3" depois de marcar três
 * ministérios é o que a pessoa acabou de fazer; "1" seria a tela contando outra
 * coisa.
 */
export function countActiveFilters(filters: CalendarFilters): number {
  return (
    (filters.coordinationIds?.length ?? 0) +
    (filters.ministryIds?.length ?? 0) +
    (filters.spaceNames?.length ?? 0) +
    (filters.statuses?.length ?? 0)
  );
}

/**
 * As opções dos seletores, **derivadas do que está na tela** (§9).
 *
 * `useProjectFilterOptions` lista o que a RLS dá — e para o líder isso é o
 * próprio ministério, o que produziria um seletor que não casa com o que ele
 * está vendo agora que a fonte "Projetos" traz a filial inteira. Aqui as opções
 * saem dos itens carregados no intervalo.
 *
 * Efeito colateral desejado: o seletor **nunca nomeia algo que a pessoa não pode
 * ver**, sem precisar de uma segunda regra de autorização escrita em lugar
 * nenhum.
 */
export interface CalendarFilterOptions {
  coordinations: readonly { id: string; name: string }[];
  ministries: readonly { id: string; name: string; coordination_id: string }[];
  spaceNames: readonly string[];
  /**
   * Só as situações PRESENTES: filtro que só pode devolver vazio não é oferecido.
   * Na ORDEM da esteira, não na de chegada — a folha as empilha uma sob a outra,
   * e uma lista que muda de ordem a cada mês obriga a reler o que já se sabia.
   */
  statuses: readonly ProjectStatus[];
}

export function filterOptionsFrom(items: readonly CalendarItem[]): CalendarFilterOptions {
  const coordinations = new Map<string, string>();
  const ministries = new Map<string, { name: string; coordination_id: string }>();
  const spaceNames = new Set<string>();
  const statuses = new Set<ProjectStatus>();

  for (const item of items) {
    if (item.kind === 'occurrence') {
      for (const name of item.occurrence.space_names) spaceNames.add(name);
      continue;
    }
    const { project } = item;
    coordinations.set(project.coordination_id, project.coordination_name);
    ministries.set(project.ministry_id, {
      name: project.ministry_name,
      coordination_id: project.coordination_id,
    });
    for (const name of project.space_names) spaceNames.add(name);
    statuses.add(project.status);
  }

  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'pt-BR');

  return {
    coordinations: [...coordinations].map(([id, name]) => ({ id, name })).sort(byName),
    ministries: [...ministries]
      .map(([id, value]) => ({ id, ...value }))
      .sort(byName),
    spaceNames: [...spaceNames].sort((a, b) => a.localeCompare(b, 'pt-BR')),
    statuses: PROJECT_STATUSES.filter((status) => statuses.has(status)),
  };
}

/**
 * Encolher a lista de ministérios pelas coordenações escolhidas.
 *
 * ⚠️ Esta é **a única regra realmente comum** entre esta barra e a de
 * `/projetos` (§9), e por isso ela vive em `shared/lib/filterScope.ts` como
 * função pura. Reexportada aqui só para a barra do calendário ter um import só.
 */
export { narrowMinistriesByCoordinations } from '@/shared/lib/filterScope';
